import { useState, useEffect } from 'react';
import { authService } from '@/lib/authService';
import type { User } from '@/types/auth';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { UserPlus, Trash, Key, ShieldCheck, User as UserIcon, PencilSimple, Sliders } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PermissionsEditor } from './PermissionsEditor';
import type { Permissions } from '@/types/auth';

import { DEFAULT_ADMIN_PERMISSIONS, DEFAULT_STAFF_PERMISSIONS } from '@/types/auth';

export const UserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'staff' | 'boxer' | 'sponsor' | 'legal-team'>('staff');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [changePasswordUserId, setChangePasswordUserId] = useState<string | null>(null);
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingUserRole, setEditingUserRole] = useState<'admin' | 'staff' | 'boxer' | 'sponsor' | 'legal-team'>('staff');
  const [editingPermissionsUserId, setEditingPermissionsUserId] = useState<string | null>(null);

  const currentSession = authService.getSession();

  const refreshUsers = async () => {
    const allUsers = await authService.getAllUsers();
    setUsers(allUsers);
  };

  // Load users on mount
  useEffect(() => {
    refreshUsers();
  }, []);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if username already exists
    const existing = users.find(u => u.username === newUsername);
    if (existing) {
      toast.error('Username already exists');
      return;
    }

    await authService.createUser(newUsername, newPassword, newRole);
    toast.success(`User "${newUsername}" created successfully`);
    
    setNewUsername('');
    setNewPassword('');
    setNewRole('staff');
    setShowAddDialog(false);
    await refreshUsers();
  };

  const handleDeleteUser = async (userId: string, username: string) => {
    if (userId === currentSession?.user.id) {
      toast.error("You can't delete your own account");
      return;
    }

    if (confirm(`Are you sure you want to delete user "${username}"?`)) {
      await authService.deleteUser(username);
      toast.success(`User "${username}" deleted`);
      await refreshUsers();
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!changePasswordUserId) return;
    
    const user = users.find(u => u.id === changePasswordUserId);
    if (!user) return;
    
    const success = await authService.updatePassword(user.username, newPasswordInput);
    
    if (success) {
      toast.success('Password updated successfully');
      setChangePasswordUserId(null);
      setNewPasswordInput('');
    } else {
      toast.error('Failed to update password');
    }
  };

  const handleUpdateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingUserId) return;

    if (editingUserId === currentSession?.user.id) {
      toast.error("You can't change your own role");
      return;
    }
    
    const user = users.find(u => u.id === editingUserId);
    if (!user) return;
    
    const success = await authService.updateUserRole(user.username, editingUserRole);
    
    if (success) {
      toast.success('User role updated successfully');
      setEditingUserId(null);
      await refreshUsers();
      // If we changed the role, update their session
      await authService.updateSession();
    } else {
      toast.error('Failed to update user role');
    }
  };

  const handleUpdatePermissions = async (permissions: Permissions) => {
    if (!editingPermissionsUserId) return;
    
    const user = users.find(u => u.id === editingPermissionsUserId);
    if (!user) return;
    
    const success = await authService.updateUserPermissions(user.username, permissions);
    
    if (success) {
      toast.success('Permissions updated! Changes are active immediately.');
      setEditingPermissionsUserId(null);
      await refreshUsers();
      
      // Trigger a custom event to notify App.tsx to reload permissions
      window.dispatchEvent(new CustomEvent('permissionsUpdated', { 
        detail: { username: user.username } 
      }));
    } else {
      toast.error('Failed to update user permissions');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl uppercase text-secondary tracking-wide">
            User Management
          </h2>
          <p className="text-muted-foreground mt-1">
            Manage user accounts and permissions
          </p>
        </div>

        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                Add a new user to the system with admin or staff access
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="Enter username"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={newRole} onValueChange={(value) => setNewRole(value as 'admin' | 'staff' | 'boxer' | 'sponsor' | 'legal-team')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin (Full Access)</SelectItem>
                    <SelectItem value="staff">Staff (Limited Access)</SelectItem>
                    <SelectItem value="boxer">Boxer (Fighter View)</SelectItem>
                    <SelectItem value="sponsor">Sponsor (Sponsor View)</SelectItem>
                    <SelectItem value="legal-team">Legal Team (Document Access)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" className="w-full">
                Create User
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            {users.length} user{users.length !== 1 ? 's' : ''} in the system
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 sm:p-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[120px]">Username</TableHead>
                  <TableHead className="min-w-[100px]">Role</TableHead>
                  <TableHead className="hidden md:table-cell min-w-[100px]">Created</TableHead>
                  <TableHead className="hidden lg:table-cell min-w-[120px]">Last Login</TableHead>
                  <TableHead className="text-right min-w-[140px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <UserIcon className="w-4 h-4 text-muted-foreground" />
                      {user.username}
                      {user.id === currentSession?.user.id && (
                        <span className="text-xs text-muted-foreground">(You)</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Dialog
                      open={editingUserId === user.id}
                      onOpenChange={(open) => {
                        if (!open) {
                          setEditingUserId(null);
                        } else {
                          setEditingUserId(user.id);
                          setEditingUserRole(user.role);
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <button
                          className="flex items-center gap-2 hover:bg-muted/50 p-2 rounded transition-colors"
                          disabled={user.id === currentSession?.user.id}
                        >
                          {user.role === 'admin' ? (
                            <ShieldCheck className="w-4 h-4 text-primary" />
                          ) : (
                            <UserIcon className="w-4 h-4 text-muted-foreground" />
                          )}
                          <span className="capitalize">{user.role}</span>
                          {user.id !== currentSession?.user.id && (
                            <PencilSimple className="w-3 h-3 text-muted-foreground" />
                          )}
                        </button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Change User Role</DialogTitle>
                          <DialogDescription>
                            Update role for {user.username}
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleUpdateRole} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="edit-role">Role</Label>
                            <Select value={editingUserRole} onValueChange={(value) => setEditingUserRole(value as 'admin' | 'staff' | 'boxer' | 'sponsor' | 'legal-team')}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">Admin (Full Access)</SelectItem>
                                <SelectItem value="staff">Staff (Limited Access)</SelectItem>
                                <SelectItem value="boxer">Boxer (Fighter View)</SelectItem>
                                <SelectItem value="sponsor">Sponsor (Sponsor View)</SelectItem>
                                <SelectItem value="legal-team">Legal Team (Document Access)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button type="submit" className="w-full">
                            Update Role
                          </Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1 sm:gap-2">
                      <Dialog
                        open={editingPermissionsUserId === user.id}
                        onOpenChange={(open) => {
                          if (!open) {
                            setEditingPermissionsUserId(null);
                          }
                        }}
                      >
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingPermissionsUserId(user.id)}
                            title="Edit Permissions"
                            className="h-8 w-8 p-0 sm:h-9 sm:w-9"
                          >
                            <Sliders className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Edit Permissions for {user.username}</DialogTitle>
                            <DialogDescription>
                              Customize what this user can see and do in the system
                            </DialogDescription>
                          </DialogHeader>
                          <PermissionsEditor
                            permissions={user.permissions || (user.role === 'admin' ? DEFAULT_ADMIN_PERMISSIONS : DEFAULT_STAFF_PERMISSIONS)}
                            onSave={handleUpdatePermissions}
                            onCancel={() => setEditingPermissionsUserId(null)}
                          />
                        </DialogContent>
                      </Dialog>

                      <Dialog
                        open={changePasswordUserId === user.id}
                        onOpenChange={(open) => {
                          if (!open) {
                            setChangePasswordUserId(null);
                            setNewPasswordInput('');
                          }
                        }}
                      >
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setChangePasswordUserId(user.id)}
                            title="Change Password"
                            className="h-8 w-8 p-0 sm:h-9 sm:w-9"
                          >
                            <Key className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Change Password</DialogTitle>
                            <DialogDescription>
                              Update password for {user.username}
                            </DialogDescription>
                          </DialogHeader>
                          <form onSubmit={handleChangePassword} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="new-password">New Password</Label>
                              <Input
                                id="new-password"
                                type="password"
                                value={newPasswordInput}
                                onChange={(e) => setNewPasswordInput(e.target.value)}
                                placeholder="Enter new password"
                                required
                              />
                            </div>
                            <Button type="submit" className="w-full">
                              Update Password
                            </Button>
                          </form>
                        </DialogContent>
                      </Dialog>

                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteUser(user.id, user.username)}
                        disabled={user.id === currentSession?.user.id}
                        title="Delete User"
                        className="h-8 w-8 p-0 sm:h-9 sm:w-9"
                      >
                        <Trash className="w-3 h-3 sm:w-4 sm:h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}             </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-lg">Role Permissions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <strong className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4 text-primary" />
              Admin
            </strong>
            <p className="text-muted-foreground ml-6">
              Full access - can register boxers, generate fight cards, manage sponsors, declare results, manage users
            </p>
          </div>
          <div>
            <strong className="flex items-center gap-2 mb-1">
              <UserIcon className="w-4 h-4 text-muted-foreground" />
              Staff
            </strong>
            <p className="text-muted-foreground ml-6">
              View-only access - can view dashboard, leaderboard, upcoming fights, and boxer profiles
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
